import React, { useState, useEffect, useRef } from "react";
import NotificationServices from "../../services/NotificationService";
import { isFeatureFlagEnabled, trackEvent } from "../../atlas-utils";
import { TRACKING_EVENT_NAMES } from "../../client-config";

const ExplainWithAI = ({ payload = {}, src = "" }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [explanation, setExplanation] = useState("");
	const [displayedText, setDisplayedText] = useState("");
	const [showInlineBox, setShowInlineBox] = useState(true);
	const [isStreaming, setIsStreaming] = useState(false);
	const hasExplainedRef = useRef(false);

	// Token-by-token display effect
	useEffect(() => {
		if (!explanation || !isStreaming) return;

		let currentIndex = 0;

		// Local variable to accumulate text
		let text = "";

		const streamText = () => {
			if (currentIndex < explanation.length) {
				text += explanation[currentIndex];
				setDisplayedText(text); // Set the whole accumulated string
				currentIndex++;
				setTimeout(streamText, 30); // Adjust speed
			} else {
				setIsStreaming(false);
			}
		};

		streamText();
	}, [explanation, isStreaming]);

	// Auto-trigger explanation on render with payload (only once)
	useEffect(() => {
		if (
			payload &&
			Object.keys(payload).length > 0 &&
			!hasExplainedRef.current &&
			isFeatureFlagEnabled("explain-JSON-with-AI")
		) {
			hasExplainedRef.current = true;
			handleExplain();
		}
	}, [payload]);

	const handleExplain = async () => {
		if (!payload) {
			NotificationServices.pushNotification({
				message: "Something went wrong! Contact our team",
				timeout: 5000,
				type: "error",
				isClosable: true,
				theme: "dark"
			});
			return;
		}

		setIsLoading(true);
		setShowInlineBox(true);
		setExplanation("");
		setDisplayedText("");

		try {
			const ATLAS_REQ_URL = process.env.REACT_APP_API_EXPLAIN_JSON;
			const response = await fetch(`${ATLAS_REQ_URL}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({ payload })
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			const explanationText = data.explanation || "No explanation provided";
			trackEvent(TRACKING_EVENT_NAMES.PIP_JSON_EXPLAIN, {
				payload,
				response: data,
				source: src
			});
			setExplanation(explanationText);
			setIsStreaming(true);
		} catch (error) {
			NotificationServices.pushNotification({
				message: "Something went wrong! Contact our team",
				timeout: 5000,
				type: "error",
				isClosable: true,
				theme: "dark"
			});
			setExplanation("Failed to get explanation. Please try again.");
			setIsStreaming(true);
		} finally {
			setIsLoading(false);
		}
	};

	const closeInlineBox = () => {
		setShowInlineBox(false);
		setExplanation("");
		setDisplayedText("");
		setIsStreaming(false);
	};
	return isFeatureFlagEnabled("explain-JSON-with-AI") ? (
		<div className="explain-wrapper">
			<div className="explain-header">
				{showInlineBox ? (
					<h4 className="explanation-title">
						Explanation by PIP{" "}
						<span role="img" aria-label="sparkles">
							✨
						</span>
					</h4>
				) : (
					<div></div>
				)}
				{/* <span onClick={handleExplain} disabled={isLoading} className="explain-with-AI">
					{isLoading ? "" : "Explain with PIP ✨"}
				</span> */}
			</div>
			{showInlineBox && (
				<div className="inline-box">
					<button onClick={closeInlineBox} className="close-button">
						×
					</button>
					<div className="explanation-content">
						{isLoading ? (
							<div className="card-container">
								<div className="card-header">
									<div className="form-row">
										<div className="shimmer H(30px) W(90%)" />
									</div>
								</div>
								<div className="card-content">
									<div className="form-row row-half">
										<div>
											<div className="shimmer H(20px) W(150px)" />
										</div>
									</div>
								</div>
							</div>
						) : (
							<>
								{displayedText}
								{isStreaming && <span className="cursor"></span>}
							</>
						)}
					</div>
					<div className="explanation-footer">AI-generated content may contain errors.</div>
				</div>
			)}
		</div>
	) : (
		<div></div>
	);
};

export default ExplainWithAI;
